/**
 * CMS-typed fallback page-level content for the scene selection page.
 *
 * Keyed by SessionTypeKey. The CMS is the source of truth; this record
 * provides a safety net when CMS content is missing for a session type.
 * Fields use the same flat/string format as the CMS type so both paths
 * are interchangeable — no parsing needed.
 */
import type { SessionTypeKey } from '@/stores/sessionType'
import { SessionType } from '@/stores/sessionType'

import type { SceneSelectionPage as CmsSceneSelectionPage } from '@VelocitySquared/sunny16-content-dataobjects-cady/booking'

import {
  rsPlusCaptureOneMoreModalFallback,
  rsPlusFreeDigitalModalFallback,
  rsPlusUpgradeToStudioModalFallback
} from '@/content/scene-selection/rsPlusSceneSelectionUpgradeModalContent'
import { rsPlusSchoolScenesContentFallback } from '@/content/scene-selection/rsPlusSchoolScenesContent'
import { rsPlusSeeTheDifferenceContentFallback } from '@/content/scene-selection/rsPlusSeeTheDifferenceContent'

// Shared RS+-only fallback content spread into every tier an RS+ user
// can land on (Basic / Simple / Standard / Legacy). Copy + assets are
// identical across RS+ tiers — the distinguishing upgrade copy lives on
// the per-tier `*ScenesUpgradeLabel` fields already handled above.
const rsPlusContentFallback = {
  schoolScenesContentRoadshowPlus: rsPlusSchoolScenesContentFallback,
  studioLockedScenesCaptionRoadshowPlus: '',
  seeTheDifferenceContentRoadshowPlus: rsPlusSeeTheDifferenceContentFallback,
  rsPlusUpgradeToStudioModal: rsPlusUpgradeToStudioModalFallback,
  rsPlusCaptureOneMoreModal: rsPlusCaptureOneMoreModalFallback,
  rsPlusFreeDigitalModal: rsPlusFreeDigitalModalFallback
}

export const sceneSelectionPageFallbacks = {
  [SessionType.Basic]: {
    internalName: '',
    identifier: '',
    subtitle: '',
    heading: '',
    sessionName: '',
    sessionFeatures: '',
    seeDetailsLabel: '',
    ctaBannerHeading: '',
    ctaBannerButtonLabel: '',
    footerReadyLabel: '',
    footerSceneSuffix: '',
    studioScenesUpgradeLabel: '',
    outdoorScenesUpgradeLabel: '',
    destinationScenesUpgradeLabel: '',
    ...rsPlusContentFallback
  },
  [SessionType.BasicPlus]: {
    internalName: '',
    identifier: '',
    subtitle: '',
    heading: '',
    sessionName: '',
    sessionFeatures: '',
    seeDetailsLabel: '',
    ctaBannerHeading: '',
    ctaBannerButtonLabel: '',
    footerReadyLabel: '',
    footerSceneSuffix: '',
    studioScenesUpgradeLabel: '',
    outdoorScenesUpgradeLabel: '',
    destinationScenesUpgradeLabel: '',
    ...rsPlusContentFallback
  },
  [SessionType.BasicMax]: {
    internalName: '',
    identifier: '',
    subtitle: '',
    heading: '',
    sessionName: '',
    sessionFeatures: '',
    seeDetailsLabel: '',
    ctaBannerHeading: '',
    ctaBannerButtonLabel: '',
    footerReadyLabel: '',
    footerSceneSuffix: '',
    studioScenesUpgradeLabel: '',
    outdoorScenesUpgradeLabel: '',
    destinationScenesUpgradeLabel: '',
    ...rsPlusContentFallback
  },
  [SessionType.Simple]: {
    internalName: '',
    identifier: '',
    subtitle: '',
    heading: '',
    sessionName: '',
    sessionFeatures: '',
    seeDetailsLabel: '',
    ctaBannerHeading: '',
    ctaBannerButtonLabel: '',
    footerReadyLabel: '',
    footerSceneSuffix: '',
    studioScenesUpgradeLabel: '',
    outdoorScenesUpgradeLabel: '',
    destinationScenesUpgradeLabel: '',
    ...rsPlusContentFallback
  },
  [SessionType.Standard]: {
    internalName: '',
    identifier: '',
    subtitle: '',
    heading: '',
    sessionName: '',
    sessionFeatures: '',
    seeDetailsLabel: '',
    ctaBannerHeading: '',
    ctaBannerButtonLabel: '',
    footerReadyLabel: '',
    footerSceneSuffix: '',
    studioScenesUpgradeLabel: '',
    outdoorScenesUpgradeLabel: '',
    destinationScenesUpgradeLabel: '',
    ...rsPlusContentFallback
  },
  [SessionType.Legacy]: {
    internalName: '',
    identifier: '',
    subtitle: '',
    heading: '',
    sessionName: '',
    sessionFeatures: '',
    seeDetailsLabel: '',
    ctaBannerHeading: '',
    ctaBannerButtonLabel: '',
    footerReadyLabel: '',
    footerSceneSuffix: '',
    studioScenesUpgradeLabel: '',
    outdoorScenesUpgradeLabel: '',
    destinationScenesUpgradeLabel: '',
    ...rsPlusContentFallback
  },
  [SessionType.MostPopular]: {
    internalName: '',
    identifier: '',
    subtitle: '',
    heading: '',
    sessionName: '',
    sessionFeatures: '',
    seeDetailsLabel: '',
    ctaBannerHeading: '',
    ctaBannerButtonLabel: '',
    footerReadyLabel: '',
    footerSceneSuffix: '',
    studioScenesUpgradeLabel: '',
    outdoorScenesUpgradeLabel: '',
    destinationScenesUpgradeLabel: ''
  },
  [SessionType.Elite]: {
    internalName: '',
    identifier: '',
    subtitle: '',
    heading: '',
    sessionName: '',
    sessionFeatures: '',
    seeDetailsLabel: '',
    ctaBannerHeading: '',
    ctaBannerButtonLabel: '',
    footerReadyLabel: '',
    footerSceneSuffix: '',
    studioScenesUpgradeLabel: '',
    outdoorScenesUpgradeLabel: '',
    destinationScenesUpgradeLabel: ''
  },
  [SessionType.ElitePlus]: {
    internalName: '',
    identifier: '',
    subtitle: '',
    heading: '',
    sessionName: '',
    sessionFeatures: '',
    seeDetailsLabel: '',
    ctaBannerHeading: '',
    ctaBannerButtonLabel: '',
    footerReadyLabel: '',
    footerSceneSuffix: '',
    studioScenesUpgradeLabel: '',
    outdoorScenesUpgradeLabel: '',
    destinationScenesUpgradeLabel: ''
  },
  [SessionType.EliteUnlimited]: {
    internalName: '',
    identifier: '',
    subtitle: '',
    heading: '',
    sessionName: '',
    sessionFeatures: '',
    seeDetailsLabel: '',
    ctaBannerHeading: '',
    ctaBannerButtonLabel: '',
    footerReadyLabel: '',
    footerSceneSuffix: '',
    studioScenesUpgradeLabel: '',
    outdoorScenesUpgradeLabel: '',
    destinationScenesUpgradeLabel: ''
  }
} as unknown as Partial<Record<SessionTypeKey, CmsSceneSelectionPage>>
